import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import { BugReport, Minimize } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useHasAccess } from '@/hooks/useHasAccess';
import { selectCurrentOutletDrawer } from '@/store/slices/storeSlice';

const ContextDebugger: React.FC = () => {
  const { currentStore, currentOutlet, stores } = useHasAccess();
  const { outletDrawerId, outletDrawerName } = useSelector(selectCurrentOutletDrawer);

  const [isMinimized, setIsMinimized] = useState(() => {
    // Load from localStorage on mount
    const saved = localStorage.getItem('debugger-minimized');
    return saved === 'true';
  });

  const effectiveStoreId = currentStore?.id || (stores.length > 0 ? stores[0].id : undefined);
  const effectiveOutletId = currentOutlet?.id;
  // Determine visibility:
  // 1. If VITE_ENV is set:
  //    - 'production': hide
  //    - 'development': show
  //    - other: fallback to other checks
  // 2. If VITE_SHOW_DEBUGGER is set, respect it
  // 3. Otherwise, default to showing in standard DEV mode
  let showDebugger = false;

  const env = import.meta.env.VITE_ENV;

  if (env === 'production') {
    showDebugger = false;
  } else if (env === 'development') {
    showDebugger = true;
  } else if (import.meta.env.VITE_SHOW_DEBUGGER !== undefined) {
    showDebugger = import.meta.env.VITE_SHOW_DEBUGGER === 'true';
  } else {
    showDebugger = import.meta.env.DEV;
  }

  if (!showDebugger) {
    return null;
  }

  // Save to localStorage when state changes
  useEffect(() => {
    localStorage.setItem('debugger-minimized', String(isMinimized));
  }, [isMinimized]);

  if (isMinimized) {
    return (
      <Tooltip title="Show Session Debugger" placement="left">
        <IconButton
          onClick={() => setIsMinimized(false)}
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            zIndex: 99999,
            bgcolor: 'error.main',
            color: 'white',
            width: 48,
            height: 48,
            boxShadow: 3,
            '&:hover': {
              bgcolor: 'error.dark',
              transform: 'scale(1.05)',
            },
            transition: 'all 0.2s',
          }}
        >
          <BugReport />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Box sx={{
      p: 1.5,
      m: 1,
      border: "2px dashed red",
      bgcolor: "rgba(255, 240, 240, 0.95)",
      borderRadius: 2,
      position: 'fixed',
      bottom: 20,
      right: 20,
      zIndex: 99999,
      maxWidth: 300,
      minWidth: 180,
      boxShadow: 3,
      transition: 'all 0.3s ease-in-out',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="subtitle2" color="error" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>
          🐞 SESSION DEBUGGER
        </Typography>
        <Tooltip title="Minimize">
          <IconButton
            size="small"
            onClick={() => setIsMinimized(true)}
            sx={{
              p: 0.5,
              color: 'error.main',
              '&:hover': {
                bgcolor: 'rgba(211, 47, 47, 0.1)',
                transform: 'rotate(90deg)',
              },
              transition: 'all 0.2s',
            }}
          >
            <Minimize sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Box>
          <Typography variant="caption" display="block" fontWeight="bold" sx={{ fontSize: '0.7rem' }}>
            Store: {currentStore?.name}
          </Typography>
          <Typography variant="caption" fontFamily="monospace" sx={{ fontSize: '0.65rem' }}>
             ID: {effectiveStoreId || "Undefined"}
          </Typography>
        </Box>
        <Box>
           <Typography variant="caption" display="block" fontWeight="bold" sx={{ fontSize: '0.7rem' }}>
            Outlet: {currentOutlet?.name || "None"}
          </Typography>
          <Typography variant="caption" fontFamily="monospace" sx={{ fontSize: '0.65rem' }}>
             ID: {effectiveOutletId || "None"}
          </Typography>
        </Box>
        <Box>
           <Typography variant="caption" display="block" fontWeight="bold" sx={{ fontSize: '0.7rem', color: outletDrawerId ? 'inherit' : 'text.secondary' }}>
            Drawer: {outletDrawerName || (outletDrawerId ? "Unknown" : "None Selected")}
          </Typography>
          <Typography variant="caption" fontFamily="monospace" sx={{ fontSize: '0.65rem' }}>
             ID: {outletDrawerId || "None"}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ContextDebugger;
